export type PermissionLevel = "full" | "view" | "comment" | "event" | "none";

/**
 * Ordered cycle for click-to-cycle matrix cell interaction.
 * Per PRO-413 design prototype: Full -> View -> Comment -> Event -> None -> Full ...
 */
export const PERMISSION_LEVEL_CYCLE: PermissionLevel[] = [
	"full",
	"view",
	"comment",
	"event",
	"none",
];

/**
 * "event" is a display-only level (a server clamp target for base roles) —
 * it is not standing-assignable. clampToGranterCeiling maps a null standing
 * rank to "none", so a user cycling to "event" and saving would always come
 * back "none" with clamped:true. Excluded from the click-to-cycle sequence.
 */
export const EDITABLE_PERMISSION_LEVEL_CYCLE: PermissionLevel[] = PERMISSION_LEVEL_CYCLE.filter(
	(level) => level !== "event"
);

/**
 * PRO-709 — RBAC v2 module keys the FE gates surfaces on, as sent in the
 * `modules` map of GET /api/v2/rbac/me/permissions.
 *
 * The single `settings` module was split per surface, because "can administer
 * the company" and "can edit the RFx scoring instructions" are different
 * grants: `settings` now covers ONLY residual company administration (users,
 * company 2FA governance, notification configuration) and each RFx settings
 * surface carries its own key. The four evaluation tabs (submission compliance,
 * evaluation permissions, scoring instructions, panel member disclaimer) share
 * ONE key by design — they are one administrative concern.
 *
 * A key missing from the payload resolves to NO access (see `useModuleCan`),
 * which is what the FE sees until the backend split ships. That is deliberate:
 * a "default to allow" fallback would be a hole if the backend were delayed.
 */
export const RBAC_MODULE_KEYS = {
	pdfReportSettings: "pdf_report_settings",
	rfxSettingsEvaluation: "rfx_settings_evaluation",
	rfxSettingsQuoteTemplates: "rfx_settings_quote_templates",
	rfxSettingsScheduleOfRates: "rfx_settings_schedule_of_rates",
	settings: "settings",
} as const;

export type RbacModuleKey = (typeof RBAC_MODULE_KEYS)[keyof typeof RBAC_MODULE_KEYS];

export interface RoleResource {
	id: number;
	uuid: string | null;
	name: string;
	side: string;
	is_base: boolean;
	cloned_from_role_id: number | null;
	company_id: number | null;
}

export interface ModuleResource {
	id: number;
	key: string;
	name: string;
	applies_to: string;
	is_future: boolean;
	available: boolean;
}

export interface RoleMatrixEntry {
	module_id: number;
	module_key: string;
	module_name: string;
	level: PermissionLevel;
	clamped: boolean;
}

export interface RoleMatrixResource {
	role_id: number;
	entries: RoleMatrixEntry[];
}

export interface CloneRolePayload {
	source_role_id: number;
	name: string;
}

export interface CreateRolePayload {
	name: string;
}

// UM "view access" (PRO-413 #UM1) — a user's server-authoritative EFFECTIVE
// standing permissions, mirroring App\Http\Resources\Rbac\EffectivePermissionsResource
// (GET /rbac/users/{user}/permissions). Standing-only (H1): resource/event-scoped
// grants are NOT represented here — this is the "what can they do company-wide" view.
export interface EffectiveCapabilityFlags {
	approve: boolean;
	close: boolean;
	comment: boolean;
	create: boolean;
	delete: boolean;
	edit: boolean;
	view: boolean;
}

export interface EffectiveModuleCapability {
	can: EffectiveCapabilityFlags;
	level: PermissionLevel;
}

export interface UserEffectivePermissions {
	// Keyed by module `key` (see ModuleResource.key).
	modules: Record<string, EffectiveModuleCapability>;
	roles: string[];
}

export interface RenameRolePayload {
	name: string;
}

export interface UpdateRoleMatrixPayload {
	entries: Array<{
		module_id: number;
		level: PermissionLevel;
	}>;
}

export const RBAC_PRIVILEGE_CLAMPED_ERROR_CODE = "rbac.privilege_clamped";

export interface RbacApiErrorResponse {
	error?: string;
	message?: string;
}

// Shared RBAC user-assignment types for the RolesPermissions feature (PRO-413 / TASK-013).
// Contracts frozen by TASK-011 (§3), verified field-for-field against the merged API resources.

export type MigrationStatus = "migrated" | "pending" | "review";

// ScopeType enum (App\Enums\Rbac\ScopeType). Same enum backs the role-assignment scope_type,
// the catalogue scope_type, and the grant context_type.
export type ScopeType = "global" | "project_type" | "project" | "rfx";

/**
 * All backing values of ScopeType, in the backend enum's declaration order
 * (App\Enums\Rbac\ScopeType::cases()). Pinned by a drift-guard test (L2) —
 * see PermissionLevel/PERMISSION_LEVEL_CYCLE for the same pattern.
 */
export const SCOPE_TYPE_VALUES: ScopeType[] = ["global", "project_type", "project", "rfx"];

// Level enum (App\Enums\Rbac\Level) — the grant/permission level.
export type ScopeLevel = "full" | "view" | "comment" | "event" | "none";

// C1 (verified vs 011): one scope per role-assignment ROW — scope lives on the assignment, not the user.
export interface UserRoleAssignment {
	assignment_id: number;
	role_id: number;
	role_name: string;
	scope_type: ScopeType;
	scope_id: number | null;
	scope_label: string | null;
}

// C14: template_creation is a per-USER flag (user boolean = ANY live row enabled, resolved server-side).
export interface UserFlags {
	template_creation: boolean;
}

export interface UserAssignment {
	id: number;
	name: string;
	email: string;
	migration_status: MigrationStatus;
	assignments: UserRoleAssignment[];
	flags: UserFlags;
}

// RoleResource.uuid is always null (Spatie roles table) — key on id, never uuid.
export interface Role {
	id: number;
	uuid: string | null;
	name: string;
	side?: string | null;
	is_base: boolean;
	// PRO-733: optional because it ships from the backend separately — when absent,
	// callers must treat every role as company-wide assignable (no filtering).
	is_event_scoped?: boolean;
}

// RoleTemplateResource{id, key, name, description, maps_to:{procurer,bidder,hybrid:[{id,name}]}}.
export interface TemplateRoleRef {
	id: number;
	name: string;
}

export interface RoleTemplate {
	id: number;
	key: string;
	name: string;
	description: string;
	maps_to: {
		procurer: TemplateRoleRef[];
		bidder: TemplateRoleRef[];
		hybrid: TemplateRoleRef[];
	};
}

// ScopeOptionResource{scope_type, scope_id, label, parent}[] — GET /rbac/scopes catalogue.
// `parent` is the server-derived cascade breadcrumb (never re-derive containment client-side).
export interface ScopeOptionParent {
	scope_type: ScopeType;
	scope_id: number | null;
	label: string;
}

export interface ScopeOption {
	scope_type: ScopeType;
	scope_id: number | null;
	label: string;
	parent: ScopeOptionParent | null;
}

// C12: PUT /scopes is an idempotent REPLACE — the UI sends the full desired end-state.
// Grant identity fields are context_type/context_id (== ScopeType/id, round-trip verbatim).
export interface ScopeGrantInput {
	context_type: ScopeType;
	context_id: number;
	level: ScopeLevel;
}

export interface ScopeGrant {
	id: number;
	context_type: ScopeType;
	context_id: number;
	context_label: string | null;
	level: ScopeLevel;
}

// PATCH /flags body — exactly {flag, enabled} (C14 contract-drift guard, no role_id).
export interface FlagTogglePayload {
	flag: keyof UserFlags;
	enabled: boolean;
}

// POST /roles — scope_type is REQUIRED; scope_id required unless scope_type is "global".
export interface AssignRolePayload {
	role_id: number;
	scope_type: ScopeType;
	scope_id?: number | null;
}

// Read-only permission grid shown inline during Step 2 (module × level).
export interface PermissionGridEntry {
	module_id: number;
	module_name: string;
	level: ScopeLevel;
}

export type AuditActionCategory = "security" | "rfx_mutation" | "general";

/**
 * Static mirror of the backend ContextType enum (int id -> case name), source:
 * work/PRO-413/main/api/app/Enums/ContextType.php. No listing endpoint exists
 * for this, so it is duplicated client-side — same pattern used elsewhere for
 * other RBAC constants in this codebase. Pinned by a drift-guard test
 * (RolesPermissions.type.test.ts) — if the backend enum changes, update BOTH
 * this list and that test's expected snapshot together, never one alone.
 */
export const AUDIT_CONTEXT_TYPES: Array<{ id: number; name: string }> = [
	{ id: 1, name: "CONVERSATION_RFX_PRE_SUBMISSION" },
	{ id: 2, name: "FILE_DOWNLOAD_RFX_PDF_SUMMARY" },
	{ id: 3, name: "FILE_DOWNLOAD_RFX_PDF_RESPONSE" },
	{ id: 4, name: "FILE_DOWNLOAD_CLARIFICATION_EXPORT" },
	{ id: 5, name: "CONVERSATION_CLARIFICATION" },
	{ id: 6, name: "INSURANCE_OCR" },
	{ id: 7, name: "COMPANY_DOCUMENT_CHANGE" },
	{ id: 8, name: "USER_MANAGEMENT" },
	{ id: 9, name: "RFX_PROCURER_DOCUMENT_CHANGE" },
	{ id: 10, name: "RFX_EVALUATION_PANEL_MEMBER_ACTION" },
	{ id: 11, name: "RFX_EVALUATION_PANEL_DISCLAIMER" },
	{ id: 12, name: "RFX_AWARD_DECISION_FILE" },
];

/**
 * Renders an enum CASE NAME (e.g. "USER_MANAGEMENT") as a readable label
 * (e.g. "User Management") for display in filters/table/modal.
 */
export const formatAuditContextTypeLabel = (name: string | null): string => {
	if (!name) {
		return "—";
	}

	return name
		.toLowerCase()
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
};

export interface AuditLogActor {
	principal_type: string | null;
	id: number | string | null;
	name: string | null;
}

export interface AuditLogChange {
	field: string;
	from: unknown;
	to: unknown;
}

export interface AuditLogResource {
	id: number;
	uuid: string | null;
	actor: AuditLogActor | null;
	action: string;
	action_category: AuditActionCategory;
	context_type: string | null;
	context_id: number | null;
	metadata: { changes: AuditLogChange[] };
	ip_address: string | null;
	created_at: string;
}

export interface AuditLogPaginationLinks {
	first: string | null;
	last: string | null;
	prev: string | null;
	next: string | null;
}

export interface AuditLogPaginationMeta {
	current_page: number;
	from: number | null;
	last_page: number;
	per_page: number;
	to: number | null;
	total: number;
}

export interface AuditLogResponse {
	data: AuditLogResource[];
	links: AuditLogPaginationLinks;
	meta: AuditLogPaginationMeta;
}

export interface AuditLogFilters {
	action_category?: AuditActionCategory;
	actor?: string;
	context_type?: number;
	from?: string;
	to?: string;
	page?: number;
	per_page?: number;
}
